import React from 'react';
import { cn } from '@/lib/utils';
import { photoSources, sitePhoto } from '@/lib/sitePhotos';

/** Aspect-ratio classes per ratio key (static strings for Tailwind). */
const RATIO = {
  '16x9': 'aspect-[16/9]',
  '21x9': 'aspect-[21/9]',
  '16x10': 'aspect-[16/10]',
  '4x5': 'aspect-[4/5]',
  '1x1': 'aspect-square',
  '4x3': 'aspect-[4/3]',
  '5x4': 'aspect-[5/4]',
  '6x5': 'aspect-[6/5]',
  '16x7': 'aspect-[16/7]',
};
const NAV_RATIO = {
  '16x9': 'nav:aspect-[16/9]',
  '21x9': 'nav:aspect-[21/9]',
  '16x10': 'nav:aspect-[16/10]',
  '4x5': 'nav:aspect-[4/5]',
  '1x1': 'nav:aspect-square',
  '4x3': 'nav:aspect-[4/3]',
  '5x4': 'nav:aspect-[5/4]',
  '6x5': 'nav:aspect-[6/5]',
  '16x7': 'nav:aspect-[16/7]',
};

/** Placeholder panel per section tone: the section's own colour, never a flat block of another. */
const TONE_BG = {
  paper: 'bg-brand-paper',
  sheet: 'bg-brand-sheet',
  navy: 'bg-navy',
  peach: 'bg-brand-peach',
  sage: 'bg-brand-sage',
  lilac: 'bg-brand-lilac',
  butter: 'bg-brand-butter',
};

export const SITE_IMAGE_RATIOS = Object.keys(RATIO);
export const SITE_IMAGE_TONES = Object.keys(TONE_BG);

/** The `nav` screen (800px, the site's mobile switch) as a media query. */
const NAV_MEDIA = '(min-width: 800px)';

/**
 * One photo slot (owner upgrade brief 2026-09-27 §2.3): a box with a fixed aspect ratio per
 * breakpoint (nothing shifts while it loads), the photo's own crop per breakpoint through
 * <picture> when `navRatio` names a different file ratio, the focal point from the photography
 * handoff (object-position via CSS custom properties), and while loading the section's tint with
 * the brand mark, faint. Hero photos pass `priority` (eager, high fetch priority); everything
 * else is lazy. No animation: the photo is readable the moment it paints.
 *
 * Props:
 * - `id` (required): a photo id from lib/sitePhotoData (e.g. 'HOME-HERO-01'); unknown → nothing
 * - `ratio`: the file ratio below 800px (e.g. '4x5'); `navRatio`: from 800px (default `ratio`)
 * - `box` / `navBox`: the box's ratio when it differs from the file's (a 16:9 file cropped to
 *   16:7); `fill`: no ratio at all, the box fills its positioned parent (absolute inset-0)
 * - `sizes`: the rendered width, for the browser's pick from the srcset (default 100vw)
 * - `priority`: an above-the-fold hero (the LCP image)
 * - `alt`: overrides the brief's alt text; `decorative`: empty alt (the text beside it says it)
 * - `tone`: the placeholder colour (paper, sheet, navy, peach, sage, lilac, butter; default paper)
 * - `className` (box), `imgClassName` (img), `testId`.
 */
export default function SiteImage({
  id,
  ratio,
  navRatio,
  box,
  navBox,
  fill = false,
  sizes = '100vw',
  priority = false,
  alt,
  decorative = false,
  tone = 'paper',
  className,
  imgClassName,
  testId,
}) {
  const photo = sitePhoto(id);
  const mobile = photo ? photoSources(id, ratio) : null;
  if (!photo || !mobile) return null;
  const wideRatio = navRatio || ratio;
  const wide = wideRatio !== ratio ? photoSources(id, wideRatio) : null;
  const boxRatio = box || ratio;
  const navBoxRatio = navBox || (navRatio ? navRatio : null) || box || null;
  const text = decorative ? '' : (alt ?? photo.alt ?? '');

  return (
    <div
      data-photo={id}
      data-testid={testId}
      className={cn(
        'relative overflow-hidden',
        fill ? 'absolute inset-0' : [RATIO[boxRatio] || RATIO[ratio], navBoxRatio && NAV_RATIO[navBoxRatio]],
        TONE_BG[tone] || TONE_BG.paper,
        className,
      )}
    >
      <img
        src="/brand-mark.svg"
        alt=""
        aria-hidden="true"
        data-image-placeholder=""
        className="pointer-events-none absolute inset-0 m-auto w-[22%] max-w-[72px] opacity-[0.14]"
      />
      <picture>
        {wide ? <source media={NAV_MEDIA} srcSet={wide.srcSet} sizes={sizes} width={wide.width} height={wide.height} /> : null}
        <img
          src={mobile.src}
          srcSet={mobile.srcSet}
          sizes={sizes}
          alt={text}
          aria-hidden={text ? undefined : 'true'}
          width={mobile.width}
          height={mobile.height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={priority ? 'high' : undefined}
          style={{ '--site-focal': mobile.focal, '--site-focal-nav': (wide || mobile).focal }}
          className={cn(
            'absolute inset-0 block size-full object-cover [object-position:var(--site-focal)] nav:[object-position:var(--site-focal-nav)]',
            imgClassName,
          )}
        />
      </picture>
    </div>
  );
}
